import type { BootInfo, BootDocumentParams, HostInfo } from "./type";
import { Action } from "./type";
import { isPossiblyURL, mdcppFetchAccessToken } from "./utility";
import {
  bootApplication,
  createAdaptiveLogger,
  endSessionFromHost,
  getRemoteUlsEndpoint,
  getAbsoluteTimeSinceEpoch,
  notifyHostAccessibilityLoopComplete,
  LogCategory,
  TraceLevel,
  DocRebootSupportMode,
} from "@1js/owlet-sdk";

import type {
  AdaptiveLogger,
  BootApplicationParams,
  BootDiagnostics,
  SessionConfig,
  DocumentBootInfo,
  WiseEnvironment,
  HostBootDiagnostics,
} from "@1js/owlet-sdk";
import * as Errors from "./errors";

export let logger: AdaptiveLogger | undefined;
export let uiHostConfig: any;
export let hostName: string;
export let hostClientType: string | undefined;
export let initialized = false;

const uiHostIntegrationType = "3PDocumentIntegration";

/**
 * Initializes the document. Must be invoked first in order to prepare the experience and perform the needed licensing checks.
 *
 * @param hostInfo - Information about the external partner host. See definition of HostInfo for details.
 */
export function initializeDocument(hostInfo: HostInfo): void {
  // do sanity check and initialize global variables
  if (!logger) {
    // 3P default environment as PRODUCTION and emit all the BULS to common endpoint
    const env: WiseEnvironment = "PRODUCTION";
    logger = createAdaptiveLogger({
      remoteUlsEndpoint: getRemoteUlsEndpoint(env),
    });
  }

  hostName = hostInfo.hostName;

  if (!uiHostConfig) {
    uiHostConfig = {
      // to do: if we need to support OneNote for 3P, we need to add the logic here and might need to change
      // the API depending on if we want to rely on internal call to detect file type or ask 3P to provide it
      // hideHeader: if file type is OneNote, hide the header
      // hideSearchInHeader: if file type is OneNote, hide the search in header
      hideAppLauncherInHeader: true,
      hideMeInHeader: true,
      hideChatInHeader: true,
      hideWorkflowInHeader: true,
      hideSettingsInHeader: true,
      showAppIconInHeader: true,
      useNeutralColorInHeader: true,
      supportsAtMentions: true,
      supportsAccessibilityLoop: true,
      supportsExcelExternalLinks: true,
      supportsCreateNewDocument: true,
      supportsExplicitUnload: true,
      supportsMoveToFolder: true,
      supportsCommonFilePicker: true,
      supportsShare: true,
      uiHostName: hostName,
      // sessionOriginPrefix: hostOrigin + ".UNIFIEDUIHOST.DOCUMENT" + ".", // to do: may not applicable to 3P. what do we expect host to pass here? what nomenclature to follow? can tweak it in later pr
      uiHostIntegrationType,
      openLinksInNewWindow: true,
      copyLinkEnabled: true,
      supportsCustomAuthConfig: false,
      supportsContentAssembly: false,
      uiEmbed: true,
      supportsHostUiControls: true,
      supportsDirectPrint: false,
      disableOfficeAddins: true,
      supportsDocumentReboot:
        DocRebootSupportMode.AnyDoc | DocRebootSupportMode.OtherMode,
      supportsInPlaceCreateNew: true,
      authTokenStrategy: "PairwiseBroker",
      disableEmbedViewCodeSnippet: true,
      disableCoEditInTeams: true,
    };
  }

  hostClientType = hostInfo.hostClientType ?? "web";
  initialized = true;
}

/**
 * Initiates a new Office for the Web session. Must have already called initializeDocument.
 *
 * @param bootDocumentParams - Information needed to boot the document. See definition of BootDocumentParams for details.
 * @returns Information about the session. See definition of BootInfo for details.
 */
export async function launch(
  bootDocumentParams: BootDocumentParams
): Promise<BootInfo> {
  // do sanity check, if not initialized, return error
  if (!initialized) {
    // cannot use logger here since logger is not initialized yet
    return Promise.reject({
      isBootSuccess: false,
      errorInfo: Errors.DocumentAPINotInitialized,
    });
  }

  const packageEntryTime = getAbsoluteTimeSinceEpoch();

  logger?.ULS.sendTraceTag(
    0x1e2da6d2 /* tag_4l01s */,
    LogCategory.msoulscat_Wac_UnifiedUiHost,
    TraceLevel.Important,
    `Start launch document from host: ${hostName} at timestamp: ${getAbsoluteTimeSinceEpoch()}.`
  );

  if (
    !bootDocumentParams.documentInfo ||
    !bootDocumentParams.sessionInfo ||
    !bootDocumentParams.container ||
    !bootDocumentParams.fetchAccessToken ||
    Object.keys(bootDocumentParams.documentInfo).length === 0 || // this is to check if the oject is empty or not
    Object.keys(bootDocumentParams.sessionInfo).length === 0
  ) {
    // ASSERTTAG_IGNORE_START
    logger?.ULS.sendTraceTag(
      0x1e3d3719 /* tag_4pt2z */,
      LogCategory.msoulscat_Wac_UnifiedUiHost,
      TraceLevel.Error,
      "host: " + hostName + " Invalid or empty input parameters"
    );
    // ASSERTTAG_IGNORE_FINISH

    return Promise.reject({
      isBootSuccess: false,
      errorInfo: {
        ...Errors.InvalidInputParameters,
        errorMessage: "Invalid or empty input parameters",
      },
    });
  }

  const uiHostDocumentInfo = bootDocumentParams.documentInfo;
  // Handle the case when both shareUrl and documentIdentifier are null or empty
  if (!uiHostDocumentInfo.shareUrl && !uiHostDocumentInfo.documentIdentifier) {
    // ASSERTTAG_IGNORE_START
    logger?.ULS.sendTraceTag(
      0x1e3d3718 /* tag_4pt2y */,
      LogCategory.msoulscat_Wac_UnifiedUiHost,
      TraceLevel.Error,
      `Neither documentIdentifier nor shareUrl be passed in by host: ${hostName}.`
    );
    // ASSERTTAG_IGNORE_FINISH

    return Promise.reject({
      isBootSuccess: false,
      errorInfo: {
        ...Errors.InvalidInputParameters,
        errorMessage: `Neither documentIdentifier nor shareUrl be passed in by host: ${hostName}.`,
      },
    });
  }

  // Handle the case when uihost try to boot document with an invalid shareUrl
  if (
    uiHostDocumentInfo.shareUrl &&
    !isPossiblyURL(uiHostDocumentInfo.shareUrl)
  ) {
    // ASSERTTAG_IGNORE_START
    logger?.ULS.sendTraceTag(
      0x1e3d3716 /* tag_4pt2w */,
      LogCategory.msoulscat_Wac_UnifiedUiHost,
      TraceLevel.Error,
      "host: " + hostName + " provided shareUrl is invalid"
    );
    // ASSERTTAG_IGNORE_FINISH

    return Promise.reject({
      isBootSuccess: false,
      errorInfo: {
        ...Errors.InvalidInputParameters,
        errorMessage: "Provided shareUrl is invalid",
      },
    });
  }

  // Handle the case when uihost try to boot document with invalid documentIdentifier
  if (
    uiHostDocumentInfo.documentIdentifier &&
    (!isPossiblyURL(uiHostDocumentInfo.documentIdentifier.siteUrl) ||
      uiHostDocumentInfo.documentIdentifier.sourceDoc === "")
  ) {
    logger?.ULS.sendTraceTag(
      0x1e2da21e /* tag_4l0i4 */,
      LogCategory.msoulscat_Wac_UnifiedUiHost,
      TraceLevel.Error,
      "host: " + hostName + " siteUrl invalid or sourceDoc empty"
    );

    return Promise.reject({
      isBootSuccess: false,
      errorInfo: {
        ...Errors.InvalidInputParameters,
        errorMessage: "siteUrl invalid or sourceDoc empty",
      },
    });
  }

  const action = uiHostDocumentInfo.action ?? Action.Default; // if host does not provide action, we assign default
  const url = new URL(window.location.href);
  const hostDomainUrl = url.origin;
  const documentIdentifier = uiHostDocumentInfo?.documentIdentifier;
  const docInfo: DocumentBootInfo = {
    docTenantUrl: documentIdentifier?.siteUrl, // this is the host document domain url
    action,
    sourceDoc: documentIdentifier?.sourceDoc,
    hostingPageOrigin: hostDomainUrl, // this is the host domain url to send post message back
    wdOrigin: uiHostDocumentInfo?.sessionOrigin,
    uiCulture: uiHostDocumentInfo?.uiCulture,
    dataCulture: uiHostDocumentInfo?.dataCulture,
    clickTime: uiHostDocumentInfo?.clickTime ?? new Date().getTime(),
    shareUrl: uiHostDocumentInfo?.shareUrl,
  };

  const environment: WiseEnvironment = "PRODUCTION";
  const sessionInfo: SessionConfig = {
    hostCorrelationId: bootDocumentParams.sessionInfo?.hostCorrelationId,
    wiseEnvironment: environment, // temp make it production, later when we support sovereign, need to figure out how to do the mapping
    hostClientType,
    isSynthetic: bootDocumentParams.sessionInfo?.isSynthetic ?? false,
    bootDiagnostics: {
      mdcppLaunchStartTime: packageEntryTime,
    } as HostBootDiagnostics,
  };

  const param: BootApplicationParams = {
    documentBootInfo: docInfo,
    sessionConfig: sessionInfo,
    uiHostConfig,
    getAuthToken: async (resourceUrl: string, claim?: string[]) =>
      mdcppFetchAccessToken(
        bootDocumentParams.fetchAccessToken,
        resourceUrl,
        claim
      ),
    eventHandlers: bootDocumentParams.eventHandlers ?? {},
    container: bootDocumentParams.container,
  };

  return bootApplication(param)
    .then((bootResult: BootDiagnostics) => {
      const bootInfo: BootInfo = {
        isBootSuccess: !!bootResult.isBootSuccess,
        // The errorInfo is included in BootInfo only when boot fails and the downstream SDK provides error details.
        ...(!bootResult.isBootSuccess && bootResult.errorInfo
          ? {
              errorInfo: {
                errorMessage: "Failed to boot document",
                errorType: Errors.getErrorTypeFromOWLErrorType(
                  bootResult.errorInfo.errorType
                ),
                errorName: bootResult.errorInfo.errorName,
                errorCode: bootResult.errorInfo.errorDetail?.httpErrorCode,
              },
            }
          : {}),
      };

      // ErrorInfo will always be returned if the SDK fails to boot the document.
      if (bootResult.errorInfo) {
        // ASSERTTAG_IGNORE_START
        logger?.ULS.sendTraceTag(
          0x1e3db6a0 /* tag_4p106 */,
          LogCategory.msoulscat_Wac_UnifiedUiHost,
          TraceLevel.Error,
          `Host ${hostName} failed to launch document with errorType: ${bootResult.errorInfo.errorType}, errorName: ${bootResult.errorInfo.errorName}, errorCode: ${bootResult.errorInfo.errorDetail?.httpErrorCode}.`
        );
        // ASSERTTAG_IGNORE_FINISH
      } else {
        logger?.ULS.sendTraceTag(
          0x1e2da6d1 /* tag_4l01r */,
          LogCategory.msoulscat_Wac_UnifiedUiHost,
          TraceLevel.Important,
          `Host ${hostName} successfully launched the document at timestamp: ${getAbsoluteTimeSinceEpoch()}.`
        );
      }

      return bootInfo;
    })
    .catch((error) => {
      // ASSERTTAG_IGNORE_START
      logger?.ULS.sendTraceTag(
        0x1e3db6d3 /* tag_4p11t */,
        LogCategory.msoulscat_Wac_UnifiedUiHost,
        TraceLevel.Error,
        `Host ${hostName} failed to launch document with error: ${error}.`
      );
      // ASSERTTAG_IGNORE_FINISH

      return {
        isBootSuccess: false,
        errorInfo: Errors.AppLoadFailedUnexpected,
      };
    });
}

// this API is used for 3P host to end a session
/**
 * Signals the end of the Office for the Web session.
 *
 * @returns Pending...
 * @alpha
 */
export function endSession(): Promise<string> {
  // ASSERTTAG_IGNORE_START
  logger?.ULS.sendTraceTag(
    0x1e3db6d2 /* tag_4p11s */,
    LogCategory.msoulscat_Wac_UnifiedUiHost,
    TraceLevel.Important,
    "endSession called from host: " + hostName
  );
  // ASSERTTAG_IGNORE_FINISH

  return endSessionFromHost(); // need to check the type? what the the string looks like?
}

// The API is used for 3P host to notify Microsoft that accessibility loop is complete
/**
 * Notify the Office for the Web experience whether to handle keyboard events related to focus change.
 *
 * @param navigateForward - Indicates the keyboard focus direction when the user switches between the Office app iframe
 * and the external partner host UI frame. `true` represents clockwise, otherwise `false`.
 * @returns A boolean value indicating whether the loop has completed successfully.
 */
export function accessibilityLoopComplete(navigateForward: boolean): boolean {
  // ASSERTTAG_IGNORE_START
  logger?.ULS.sendTraceTag(
    0x1e3db6d1 /* tag_4p11r */,
    LogCategory.msoulscat_Wac_UnifiedUiHost,
    TraceLevel.Important,
    "hostAccessibilityLoopComplete called from host: " + hostName
  );
  // ASSERTTAG_IGNORE_FINISH

  return notifyHostAccessibilityLoopComplete(navigateForward);
}
